<template>
  <HomeCollaborateAndRecentViewsSkeletonLoader
    v-if="loading"
    :is-admin-on="isAdminOn"
    :is-admin-role="isAdminRole"
  />
  <section
    v-else
    class="home-collaborate-and-recent-views"
    :class="{ 'is-admin-on': isAdminOn, 'is-not-admin-role': !isAdminRole }"
    aria-label="Notifications and Recently Viewed"
  >
    <div class="collaborate-and-recent-views-header">
      <HideRbac :permissions="['actOnPendingApprovals']">
        <div
          class="approve-tab text-body-2 tab"
          :class="{ active: activeTab === 'approve' }"
          @click="activeTab = 'approve'; homeClick('Approve');"
        >
          <v-icon
            icon="$clipboard-check-outline"
            :size="16"
            class="mr-2"
          />
          Approve
          <span class="notifications">{{ myPending.length }}</span>
        </div>
      </HideRbac>
      <HideRbac :permissions="['cordComments']">
        <div
          class="collaborate-tab text-body-2 tab"
          :class="{ active: activeTab === 'collaborate' }"
          @click="activeTab = 'collaborate'; homeClick('Collaborate');"
        >
          <v-icon
            icon="$people"
            :size="16"
            class="mr-2"
          />
          Collaborate
        </div>
      </HideRbac>
      <div
        class="recent-views-tab text-body-2 tab"
        :class="{ active: activeTab === 'recent-views' }"
        @click="activeTab = 'recent-views'; homeClick('Recently Viewed');"
      >
        <v-icon
          icon="$clock"
          :size="16"
          class="mr-2"
        />
        Recently Viewed
      </div>
    </div>
    <div
      v-if="isApproveGranted && activeTab === 'approve'"
      class="tab-content"
    >
      <HomeApprovalsTab />
    </div>
    <div
      v-else-if="isCollaborateGranted && activeTab === 'collaborate'"
      class="tab-content"
    >
      <HomeCollaborateTab />
    </div>
    <div
      v-else
      class="tab-content"
    >
      <HomeRecentViewsItem
        v-for="({
          timestamp, name, params, query, meta,
        }, index) in recentViewsItems"
        :key="timestamp"
        :name="name"
        :meta="meta"
        :params="params"
        :query="query"
        :timestamp="timestamp"
        :index="index"
      />
    </div>
  </section>
</template>

<script setup>
import {
  ref,
  defineAsyncComponent,
  onMounted,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useHome } from '@/stores/home/home';
import { useTracking } from '@/stores/tracking/tracking';
import { useAppAuthData } from '@/stores/app/authData';
import { useApprovalFlows } from '@/stores/jobProfiles/approvalFlows';
import HideRbac from '@/components/rbac/HideRbac.vue';
import HomeApprovalsTab from '@/components/home/HomeCollaborateAndRecentViews/HomeApprovalsTab.vue';
import HomeCollaborateAndRecentViewsSkeletonLoader
  from './HomeCollaborateAndRecentViewsSkeletonLoader.vue';
import HomeCollaborateTab from './HomeCollaborateTab.vue';

const HomeRecentViewsItem = defineAsyncComponent(() => import('./HomeRecentViewsItem.vue'));

defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  isAdminOn: {
    type: Boolean,
    default: false,
  },
  isAdminRole: {
    type: Boolean,
    default: false,
  },
});

const { recentViewsItems } = storeToRefs(useHome());

const { myPending } = storeToRefs(useApprovalFlows());
const { loadMyPending } = useApprovalFlows();

const { homeClick } = useTracking();
const { checkRbacPermissions } = useAppAuthData();

const activeTab = ref('recent-views');
const isCollaborateGranted = ref(false);
const isApproveGranted = ref(false);

onMounted(async () => {
  isCollaborateGranted.value = checkRbacPermissions(['cordComments']);
  isApproveGranted.value = checkRbacPermissions(['actOnPendingApprovals']);

  if (isCollaborateGranted.value) {
    activeTab.value = 'collaborate';
  }
  if (isApproveGranted.value) {
    activeTab.value = 'approve';
    await loadMyPending();
  }
});
</script>

<style lang="less" scoped>
.home-collaborate-and-recent-views {
  margin-top: 53px;
  width: 380px;
  height: calc(100% - 200px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;

  &.is-admin-on {
    margin-top: 117px;
  }

  &.is-not-admin-role {
    margin-top: 144px;
  }
}

.collaborate-and-recent-views-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
}

.tab {
  display: flex;
  position: relative;
  width: max-content;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  white-space: nowrap;
  padding: 12px 6px;
  color: @text-lighter;
  cursor: pointer;
  z-index: 2;
  background: fade(@grey-400, 20%);

  :deep(svg) path, :deep(svg) circle {
    stroke: @stroke-dark;
  }

  &::before {
    content: '';
    position: absolute;
    display: inline-block;
    bottom: 0px;
    left: 0px;
    width: 100%;
    height: 2px;
    background-color: fade(@grey-400, 20%);
  }

  &.active, &:hover {
    color: @text-brand;

    :deep(svg) path, :deep(svg) circle {
      stroke: @icon-brand;
    }

    &::before {
      background-color: @primary;
    }
  }
}

.approve-tab {
  .notifications {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: @text-light;
    color: @text-white;
    margin-left: 8px;
    font-size: 8px;
  }

  &.active .notifications,
  &:hover .notifications {
    background-color: @primary;
  }
}

.tab-content {
  width: 380px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  height: 600px;
  max-height: 600px;
  overflow-y: auto;
}

.collaborate-icon {
  margin-right: 10px;
}
</style>
